/**
 * useAuth — React hook wrapping Firebase Auth state. Tracks the signed-in
 * user, provides sign-in with custom token, and sign-out. Syncs the email
 * to SessionContext. British English project.
 */
import { useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signOut as fbSignOut, type User } from 'firebase/auth';
import { auth } from './firebase.js';
import { useSession } from '../context/SessionContext.js';

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

/** Sign in with a Firebase custom token (returned by the BFF verify-otp endpoint). */
export async function signInWithToken(customToken: string): Promise<User> {
  const cred = await signInWithCustomToken(auth, customToken);
  return cred.user;
}

/** Sign out the current user. */
export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

/**
 * useAuth — subscribes to Firebase auth state changes. Returns the current
 * user, loading state, and any error. Syncs the user's email to SessionContext.
 */
export function useAuth(): AuthState & { signOut: () => Promise<void> } {
  const session = useSession();
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(
      auth,
      (user) => {
        setState({ user, loading: false, error: null });
        if (user?.email) {
          session.setEmail(user.email);
        } else if (!user) {
          session.setEmail(null);
        }
      },
      (err) => {
        setState({ user: null, loading: false, error: err.message });
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    session.signOut();
  }, [session]);

  return { ...state, signOut: handleSignOut };
}